// ── CinematicLayer ────────────────────────────────────────────────────────────
// The one-shot cinematics and modals, gathered behind a single lazy boundary so
// they stop riding the entry chunk. Before this, App.jsx statically imported all
// of them, and the entry chunk was 115 KB gz — 44% of first paint — most of it
// celebration code for moments that can only happen minutes into a session
// (finishing a show, adding one, completing an import).
//
// WHY THE RACE IS SAFE. Every component here listens for a window event that is
// only ever dispatched by a USER ACTION — marking watched, adding a show,
// finishing an import (verified 2026-08-01: zero boot-time dispatch sites). App mounts this
// layer inside <Suspense fallback={null}> immediately, so the chunk starts
// fetching right after hydration; the only unlistened window is the few hundred
// ms before that fetch lands, during which the UI a user would need to trigger
// any of these hasn't painted yet either.
//
// ORDERING: these are fixed overlays with explicit z tokens, not portals, so
// their stacking against the app is z-index-governed, not DOM-order-governed.
// Their relative order here mirrors the order they had in App.jsx.
//
// If you add a new cinematic: put it HERE, not in App.jsx — unless it must catch
// an event that can fire at boot, in which case say so loudly in a comment.
import { FinalCut } from './FinalCut'
import { CanonSeal } from './CanonSeal'
import { ImportFinale } from './ImportFinale'
import { FirstConnection } from './FirstConnection'
import { WritersRoom } from './WritersRoom'
import { ClapSlamLayer, MoonRiseLayer } from './ClapboardIcon'
import { WrapCelebration } from './WrapCelebration'
import { RewatchStart } from './RewatchStart'
import { PlayRatingPrompt } from './PlayRatingPrompt'
import { KeepsakeReveal } from './KeepsakeReveal'

export default function CinematicLayer({ user, wl }) {
  return (
    <>
      {/* 17: CardReveal (Cinephile Cards) is cut; cards still mint quietly for a future return.
          The Final Cut card (KeepsakeReveal) is the one card that came back (2026-09-20). */}
      <FinalCut user={user} wl={wl} />
      <KeepsakeReveal />
      <CanonSeal />
      <ImportFinale />
      <FirstConnection user={user} />
      <WritersRoom />
      {/* 17: the post-add beat is a flat status sheet (ShowAddedSheet), mounted in
          App.jsx inside PlansProvider since 2026-09-21 so it can offer Tonight. */}
      <ClapSlamLayer />
      <MoonRiseLayer />
      <WrapCelebration />
      <RewatchStart />
      {/* Rides the celebration queue's low-priority `rating` slot, so it only
          ever appears AFTER whatever cinematic it's trading on has finished. */}
      <PlayRatingPrompt />
      {/* EpisodePulseHost intentionally NOT here — App.jsx mounts the one host
          (ErrorBoundary-wrapped, lazy). A second mount here doubled every Pulse
          listener: two peeks/sheets per episode-watched event (audit 2026-08-14). */}
    </>
  )
}
